/* eslint-disable @hakio/no-untranslated-terminology -- flag panel copy is internal (staff-only), not tenant-facing */
import type { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community"
import type { AgGridReact } from "ag-grid-react"
import { useMemo, useRef } from "react"

import { COMPANY_FLAG_COLUMNS } from "~/features/feature-flags/client/companyFlagColumns"
import {
  CategoryCell,
  FlagLink,
  useFlagFilters,
} from "~/features/feature-flags/client/FlagControls"
import { FlagPanelProvider, useFlagPanel } from "~/features/feature-flags/client/FlagPanelProvider"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { matchesFlag, NO_MATCHES } from "~/features/feature-flags/client/model/filters"
import {
  organizationFlagRows,
  type OrganizationFlagRow,
} from "~/features/feature-flags/client/model/flagPage"

function FlagCell({ data }: ICellRendererParams<OrganizationFlagRow>) {
  if (!data) return null
  return (
    <span className="min-w-0 truncate">
      <FlagLink flagKey={data.flagKey} />
    </span>
  )
}

const DEFAULT_COLUMN: ColDef = { sortable: false, suppressHeaderMenuButton: true, minWidth: 100 }

const COLUMN_DEFS: ColDef<OrganizationFlagRow>[] = [
  {
    colId: "flag",
    headerName: "Flag",
    cellRenderer: FlagCell,
    cellClass: "ag-cell-vcenter",
    flex: 1,
    minWidth: 240,
  },
  {
    colId: "category",
    headerName: "Category",
    cellRenderer: CategoryCell,
    cellClass: "ag-cell-vcenter",
    width: 120,
  },
  ...COMPANY_FLAG_COLUMNS,
]

const GRID_OPTIONS: GridOptions<OrganizationFlagRow> = {
  getRowId: ({ data }) => data.flagKey,
  suppressCellFocus: true,
}

function OrganizationFlagsGrid({ orgId }: { orgId: string }) {
  const panel = useFlagPanel()
  const gridRef = useRef<AgGridReact<OrganizationFlagRow>>(null)
  const { category, search, toolbar } = useFlagFilters({ placeholder: "Search flags" })

  const allRows = useMemo(() => organizationFlagRows(panel, orgId), [panel, orgId])
  const rows = useMemo(
    () =>
      allRows.filter(({ flagKey, definition }) =>
        matchesFlag(flagKey, definition, category, search),
      ),
    [allRows, category, search],
  )

  return (
    <div className="flex flex-col gap-3">
      {toolbar}
      <DataTableBaseV2<OrganizationFlagRow>
        id="organization-feature-flags"
        gridRef={gridRef}
        rowData={rows}
        columnDefs={COLUMN_DEFS}
        defaultColumnDef={DEFAULT_COLUMN}
        gridOptions={GRID_OPTIONS}
        loading={panel.loadingCompanies}
        autoHeight
        showPagination={false}
        disableColumnSizingPersistence
        enableCellTextSelection={false}
        emptyMessage={NO_MATCHES}
      />
    </div>
  )
}

/**
 * Every flag for one company, with its category, status, setting, source and user overrides, the
 * same as the flag page's company grid. Used on the organization page and in the Customer Success
 * page's dialog.
 */
export function OrganizationFeatureFlags({ orgId }: { orgId: string }) {
  return (
    <FlagPanelProvider>
      <OrganizationFlagsGrid orgId={orgId} />
    </FlagPanelProvider>
  )
}
